import { useCallback, useMemo } from "react";
import { nanoid as generateId } from "src/util/simpleId";
import Consts from "../consts";
import { useUserPreferences } from "./useUserPreferences";

const isValidView = (view: unknown): view is user_prefs.TeamCanvasView =>
  Boolean(
    view &&
    typeof view === "object" &&
    typeof (view as user_prefs.TeamCanvasView).id === "string" &&
    (view as user_prefs.TeamCanvasView).id &&
    typeof (view as user_prefs.TeamCanvasView).name === "string" &&
    (view as user_prefs.TeamCanvasView).name.trim() &&
    Array.isArray((view as user_prefs.TeamCanvasView).teamIds) &&
    (view as user_prefs.TeamCanvasView).teamIds.length > 0
  );

/** Saved Team Canvas views (name + team ids), per workspace user. */
export const useUserTeamCanvasViews = () => {
  const {
    mergedPreferences,
    workspaceUserPreferences,
    updatePreferencesForWorkspaceUser,
  } = useUserPreferences();

  const views = useMemo(
    () =>
      (
        mergedPreferences?.teamCanvasViews ||
        Consts.defaultPreferences.teamCanvasViews
      ).filter(isValidView),
    [mergedPreferences?.teamCanvasViews]
  );

  const updateViews = useCallback(
    (nextViews: user_prefs.TeamCanvasViews) =>
      updatePreferencesForWorkspaceUser({
        ...workspaceUserPreferences,
        teamCanvasViews: nextViews,
      }),
    [workspaceUserPreferences, updatePreferencesForWorkspaceUser]
  );

  const saveView = useCallback(
    async ({ name, teamIds }: { name: string; teamIds: string[] }) => {
      const view: user_prefs.TeamCanvasView = {
        id: generateId(),
        name: (name || "").trim(),
        teamIds: (teamIds || []).filter(Boolean),
        createdAt: new Date().toISOString(),
      };
      if (!isValidView(view)) {
        return null;
      }
      const saved = await updateViews([...views, view]);
      return saved ? view : null;
    },
    [views, updateViews]
  );

  const deleteView = useCallback(
    (viewId: string) => updateViews(views.filter((view) => view.id !== viewId)),
    [views, updateViews]
  );

  return {
    views,
    saveView,
    deleteView,
  };
};
